import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { format, parseISO } from "date-fns";
import { LeagueRules } from "@/components/leagues/LeagueRules";
import { LeagueRestricted } from "@/components/leagues/LeagueRestricted";
import { MatchDayPill } from "@/components/leagues/MatchDayPill";

export default function Leagues() {
  const { user } = useAuth();
  const [leagues, setLeagues] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = user?.role === "admin";
  const canSeeRestricted = isAdmin || !!user?.is_club_secretary;

  const load = async () => {
    const [l, t] = await Promise.all([api.get("/leagues"), api.get("/teams", { params: { all_seasons: true } })]);
    setLeagues(l.data); setTeams(t.data); setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const teamsByLeague = teams.reduce((acc, t) => {
    if (t.league_id) (acc[t.league_id] = acc[t.league_id] || []).push(t);
    return acc;
  }, {});

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#83B68B]">Directory</div>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2">Leagues</h1>
          <p className="opacity-60 mt-2 text-sm">{leagues.length} league{leagues.length !== 1 && "s"} · view contact details, fees and linked teams. {isAdmin ? "" : "Admin only can edit."}</p>
        </div>
        {isAdmin && <Link to="/app/admin" className="btn-volt px-5 py-2.5 rounded-md text-sm">Edit in Admin</Link>}
      </div>

      {loading ? (
        <div className="opacity-50 font-display tracking-widest">LOADING…</div>
      ) : leagues.length === 0 ? (
        <div className="card-tactical rounded-md p-10 text-center opacity-60">No leagues set up yet.</div>
      ) : (
        <div className="space-y-4" data-testid="leagues-list">
          {leagues.map(l => {
            const roles = [
              ["Chairman", l.chairman], ["Secretary", l.secretary],
              ["Welfare officer", l.welfare_officer], ["Fixture secretary", l.fixture_secretary],
              ["League contact", l.league_contact],
            ].filter(([_, c]) => c && (c.name || c.email || c.phone));
            const linked = teamsByLeague[l.id] || [];
            return (
              <div key={l.id} className="card-tactical rounded-md p-5" data-testid={`league-card-${l.id}`}>
                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                  <div>
                    <div className="font-display font-black text-2xl tracking-tight">{l.name}</div>
                    {l.website && <a href={l.website} target="_blank" rel="noreferrer" className="text-xs opacity-70 hover:text-[#83B68B]">{l.website}</a>}
                  </div>
                  <div className="flex items-center gap-2">
                    <MatchDayPill league={l} isAdmin={isAdmin} onChanged={load} />
                    <div className="text-[10px] opacity-50 tracking-widest uppercase">{linked.length} team{linked.length !== 1 && "s"}</div>
                  </div>
                </div>
                {l.notes && <p className="text-sm opacity-70 mt-2">{l.notes}</p>}

                {roles.length > 0 && (
                  <div className="mt-4 border-t border-border pt-3 grid sm:grid-cols-2 gap-3">
                    {roles.map(([label, c]) => (
                      <div key={label} className="text-xs">
                        <div className="text-[10px] tracking-widest uppercase opacity-50">{label}</div>
                        <div className="opacity-90 flex flex-wrap gap-x-3 mt-0.5">
                          {c.name && <span className="font-medium">{c.name}</span>}
                          {c.phone && <a href={`tel:${(c.phone || "").replace(/\s+/g, "")}`} className="opacity-70 hover:text-[#83B68B]">📞 {c.phone}</a>}
                          {c.email && <a href={`mailto:${c.email}`} className="opacity-70 hover:text-[#83B68B]">✉ {c.email}</a>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {(l.league_fee != null || l.default_qualified_fee != null || l.default_unqualified_fee != null || (l.payment_grid || []).length > 0) && (
                  <div className="mt-4 border-t border-border pt-3">
                    <div className="text-[10px] tracking-widest uppercase text-[#83B68B] mb-1">Fees</div>
                    <div className="flex flex-wrap gap-x-4 text-xs opacity-80">
                      {l.league_fee != null && <span>League fee: <strong>£{l.league_fee}</strong></span>}
                      {l.default_qualified_fee != null && <span>Qualified default: <strong>£{l.default_qualified_fee}</strong></span>}
                      {l.default_unqualified_fee != null && <span>Unqualified default: <strong>£{l.default_unqualified_fee}</strong></span>}
                    </div>
                    {(l.payment_grid || []).length > 0 && (
                      <div className="mt-2 text-[11px] opacity-70">
                        {l.payment_grid.map(r => `${r.age_group}${r.duration_min ? ` (${r.duration_min}m)` : ""} £${r.qualified_fee}/£${r.unqualified_fee}`).join(" · ")}
                      </div>
                    )}
                  </div>
                )}

                {linked.length > 0 && (
                  <div className="mt-4 border-t border-border pt-3">
                    <div className="text-[10px] tracking-widest uppercase opacity-50 mb-1">Teams</div>
                    <div className="flex flex-wrap gap-1.5">
                      {linked.map(t => (
                        <span key={t.id} className="text-[10px] tracking-widest uppercase px-2 py-1 rounded bg-[#83B68B]/10 text-[#83B68B] border border-[#83B68B]/30">
                          {t.name} · {t.age_group}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <LeagueRules league={l} isAdmin={isAdmin} />
                {canSeeRestricted && <LeagueRestricted league={l} />}

                {l.updated_at && (
                  <div className="mt-3 text-[10px] opacity-40 tracking-widest uppercase" data-testid={`league-audit-${l.id}`}>
                    Updated {format(parseISO(l.updated_at), "d MMM yyyy HH:mm")}{l.updated_by_name ? ` · ${l.updated_by_name}` : ""}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
